"use client";

import { useState, useEffect } from "react";
import { Zap, Calendar, Info, X } from "lucide-react";
import {
  Card,
  CardContent,
  Button,
  Separator,
  DateRangeInput,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Label,
  DialogClose,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toDateString,
} from "@fm/ui";
import CustomerAddressAutocomplete from "./CustomerAddressAutocomplete";
import { AddonSelector } from "./AddonSelector";
import { InsuranceOptionSelector } from "./InsuranceOptionSelector";
import {
  getLineItemLabel,
  splitLineItemsByStage,
  type Addon,
  type InsuranceOption,
  type InsuranceOptionGroup,
} from "@fm/api";
import type { VehicleDeliveryLocation } from "@fm/api/vehicle-delivery-locations";
import { REQUEST_TO_BOOK_BANK_NOTICE } from "../lib/bookingRequestCopy";
import { getStayNightlyRate } from "../lib/stay-rates";

interface DeliveryAddress {
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

interface BookingSummaryProps {
  vehicleName: string;
  pricePerNight: number;
  minStay: number;
  maxStay: number;
  checkIn?: Date;
  checkOut?: Date;
  onContinue?: () => void;
  onDatesChange?: (dates: [Date, Date] | null) => void;
  onDeliveryChange?: (
    type: "pickup" | "delivery",
    address?: DeliveryAddress,
  ) => void;
  // Predefined delivery locations for the vehicle. Each already carries its
  // own flat `delivery_fee`, so picking one skips the distance-based fee
  // calculation the custom-address path uses.
  deliveryLocations?: VehicleDeliveryLocation[];
  // Whether the vehicle offers delivery at all. When false the delivery
  // choice is disabled and marked "Not available" (same wording as
  // DeliveryOptionsSelector).
  deliveryAvailable?: boolean;
  // Whether the vehicle also accepts a typed address. When false the dialog
  // offers only `deliveryLocations` (no address form). Defaults to true.
  customAddressAvailable?: boolean;
  // The delivery type the page actually applies. When it changes (a delivery
  // switched off by a refusal or a reload) the choice shown follows it.
  deliveryType?: "pickup" | "delivery";
  onDeliveryLocationSelect?: (locationId: string) => void;
  pricingBreakdown?: import("@fm/api").PricingBreakdown | null;
  isLoadingPricing?: boolean;
  // Delivery pricing from vehicle
  deliveryBaseFee?: number;
  deliveryFeePerMile?: number;
  deliveryMaxRadius?: number;
  // Add-ons
  addons?: Addon[];
  selectedAddonIds?: string[];
  onAddonsChange?: (addonIds: string[]) => void;
  isLoadingAddons?: boolean;
  // Insurance Options
  insuranceOptions?: InsuranceOption[];
  /** Groups the options belong to; without them every option is a checkbox. */
  insuranceGroups?: InsuranceOptionGroup[];
  selectedInsuranceOptionIds?: string[];
  onInsuranceOptionsChange?: (optionIds: string[]) => void;
  isLoadingInsurance?: boolean;
  pickupLocation?: string;
  unavailableDates?: Set<string>;
  primaryColor?: string;
  initialRentalType?: "pickup" | "delivery";
  initialDeliveryAddress?: DeliveryAddress & { lat?: number; lng?: number };
  initialDeliveryOption?: "custom" | "location";
  initialDeliveryLocationId?: string;
  // Request to Book: the vehicle's EFFECTIVE booking_mode (see
  // `isRequestToBook` in bookingRequestCopy.ts). Swaps the button's label and
  // subtext to match the badge above it and the checkout step that follows.
  // Defaults to false so instant-booking callers are unaffected.
  requestToBook?: boolean;
}

export function BookingSummary({
  vehicleName = "Vehicle",
  pricePerNight = 0, // Only for initial display, real price comes from pricingBreakdown
  minStay = 1, // Conservative fallback (1 night minimum)
  maxStay = 60,
  checkIn,
  checkOut,
  onContinue,
  onDatesChange,
  onDeliveryChange,
  deliveryLocations = [],
  deliveryAvailable = true,
  customAddressAvailable = true,
  deliveryType,
  onDeliveryLocationSelect,
  pricingBreakdown,
  isLoadingPricing = false,
  deliveryBaseFee,
  deliveryFeePerMile,
  deliveryMaxRadius,
  addons = [],
  selectedAddonIds = [],
  onAddonsChange,
  isLoadingAddons = false,
  insuranceOptions = [],
  insuranceGroups = [],
  selectedInsuranceOptionIds = [],
  onInsuranceOptionsChange,
  isLoadingInsurance = false,
  pickupLocation,
  unavailableDates,
  primaryColor,
  initialRentalType,
  initialDeliveryAddress,
  initialDeliveryOption,
  initialDeliveryLocationId,
  requestToBook = false,
}: BookingSummaryProps) {
  const effectiveColor = primaryColor || "#083C91";

  const hexToRgba = (hex: string, alpha: number): string => {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = (num >> 16) & 0xff;
    const g = (num >> 8) & 0xff;
    const b = num & 0xff;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const darkenHex = (hex: string, percent: number = 10): string => {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = Math.max(0, ((num >> 16) & 0xff) - Math.round(2.55 * percent));
    const g = Math.max(0, ((num >> 8) & 0xff) - Math.round(2.55 * percent));
    const b = Math.max(0, (num & 0xff) - Math.round(2.55 * percent));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  };

  const [dateRange, setDateRange] = useState<[string, string] | null>(
    checkIn && checkOut
      ? [toDateString(checkIn), toDateString(checkOut)]
      : null,
  );
  const [rentalType, setRentalType] = useState<"pickup" | "delivery">(
    deliveryAvailable ? initialRentalType || "pickup" : "pickup",
  );
  useEffect(() => {
    if (deliveryType) setRentalType(deliveryType);
  }, [deliveryType]);
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [isAddonsModalOpen, setIsAddonsModalOpen] = useState(false);
  const [isInsuranceModalOpen, setIsInsuranceModalOpen] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState<
    DeliveryAddress & { lat?: number; lng?: number }
  >(
    initialDeliveryAddress || {
      address: "",
      city: "",
      state: "",
      zipCode: "",
    },
  );
  const [formErrors, setFormErrors] = useState<{
    address?: boolean;
    city?: boolean;
    state?: boolean;
    zipCode?: boolean;
  }>({});
  const [chosenDeliveryOption, setDeliveryOption] = useState<
    "custom" | "location"
  >(initialDeliveryOption ?? "custom");
  const [selectedLocationId, setSelectedLocationId] = useState<string>(
    initialDeliveryLocationId ?? "",
  );
  const hasDeliveryLocations = deliveryLocations.length > 0;
  // Without typed addresses the only possible choice is a location.
  const deliveryOption = customAddressAvailable
    ? chosenDeliveryOption
    : "location";
  const selectedLocation = deliveryLocations.find(
    (location) => location.id === selectedLocationId,
  );

  // Reset or update date range when props change
  useEffect(() => {
    if (checkIn && checkOut) {
      setDateRange([toDateString(checkIn), toDateString(checkOut)]);
    }
  }, [checkIn, checkOut]);

  const handleDateChange = (dates: [string, string] | null) => {
    setDateRange(dates);
    if (onDatesChange) {
      if (dates && dates[0] && dates[1]) {
        const start = new Date(dates[0] + "T00:00:00");
        const end = new Date(dates[1] + "T00:00:00");

        // Only propagate if both dates are valid
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          onDatesChange([start, end]);
        }
      } else {
        onDatesChange(null);
      }
    }
  };

  const handleRentalTypeChange = (type: "pickup" | "delivery") => {
    if (type === "delivery") {
      setIsDeliveryModalOpen(true);
    } else {
      setRentalType("pickup");
      if (onDeliveryChange) {
        onDeliveryChange("pickup");
      }
    }
  };

  const confirmDeliveryAddress = () => {
    // Basic validation
    const errors = {
      address: !deliveryAddress.address,
      city: !deliveryAddress.city,
      state: !deliveryAddress.state,
      zipCode: !deliveryAddress.zipCode,
    };

    if (errors.address || errors.city || errors.state || errors.zipCode) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    setRentalType("delivery");
    setIsDeliveryModalOpen(false);

    if (onDeliveryChange) {
      onDeliveryChange("delivery", deliveryAddress);
    }
  };

  const confirmDeliveryLocation = () => {
    if (!selectedLocationId) return;

    setRentalType("delivery");
    setIsDeliveryModalOpen(false);
    onDeliveryLocationSelect?.(selectedLocationId);
  };

  const cancelDeliverySelection = () => {
    setIsDeliveryModalOpen(false);
    // If we were not already on delivery, stay on pickup
    if (rentalType !== "delivery") {
      // do nothing, effectively cancelled selection
    }
  };

  // Mock calculation logic for fallback only
  const nights = dateRange
    ? Math.max(
        1,
        Math.ceil(
          (new Date(dateRange[1] + "T00:00:00").getTime() -
            new Date(dateRange[0] + "T00:00:00").getTime()) /
            (1000 * 60 * 60 * 24),
        ),
      )
    : 1;

  const isDateSelected = !!dateRange;

  // Header rate: the grid rate (`pricePerNight`) never includes length-of-stay
  // modifiers, so once the breakdown for THIS stay is loaded its base rental
  // (total / days, base rental only) is the real nightly rate. Until then —
  // no dates, loading, failed request, stale breakdown — keep the grid rate.
  const headerNightlyRate =
    getStayNightlyRate(pricingBreakdown, dateRange?.[0], dateRange?.[1]) ??
    pricePerNight;

  // Derive totals from pricingBreakdown if available, otherwise fallback
  const total = pricingBreakdown
    ? parseFloat(pricingBreakdown.total)
    : pricePerNight * nights; // Simple fallback

  // Helper to format miles in description text
  const formatMilesInDescription = (description: string): string => {
    // Match patterns like "(1072.7851168802117 miles)" and format to 2 decimals
    return description.replace(
      /\((\d+\.?\d*)\s+miles?\)/gi,
      (match, number) => {
        const formatted = parseFloat(number).toFixed(2);
        return `(${formatted} miles)`;
      },
    );
  };

  // Helper to find line items by type
  const renderLineItems = () => {
    if (!pricingBreakdown?.line_items) return null;

    // Splitting the list and rendering the stages is one operation: a row only
    // leaves the item list by appearing in the stage that renders it. Doing it
    // in two steps is what made tax and percentage-fee rows disappear when the
    // breakdown carried no stage sections.
    const stages = splitLineItemsByStage(
      pricingBreakdown.line_items,
      pricingBreakdown.breakdown,
    );
    const subtotalItems = stages.subtotalItems;

    return (
      <div className="space-y-3 text-sm">
        {subtotalItems.map((item, index) => (
          <div key={index} className="flex justify-between text-[#020817]">
            <span>
              {formatMilesInDescription(item.description)}
              {item.quantity && item.quantity > 1 ? ` (x${item.quantity})` : ""}
            </span>
            <span className="font-medium">
              ${parseFloat(item.total_amount).toFixed(2)}
            </span>
          </div>
        ))}
        {stages.hasStages && (
          <>
            <div className="flex justify-between text-[#020817] pt-1 border-t border-gray-200">
              <span>Subtotal</span>
              <span className="font-medium">
                ${parseFloat(pricingBreakdown.breakdown.subtotal).toFixed(2)}
              </span>
            </div>
            {stages.afterSubtotal.flatMap((stage) =>
              stage.items.map((stageItem, stageIndex) => (
                <div
                  key={
                    stageItem.metadata?.fee_public_id ??
                    `${stage.key}-${stageIndex}`
                  }
                  className="flex justify-between text-[#020817]"
                >
                  <span>{getLineItemLabel(stageItem)}</span>
                  <span className="font-medium">
                    ${parseFloat(stageItem.total_amount).toFixed(2)}
                  </span>
                </div>
              )),
            )}
          </>
        )}
        {/* Only show Edit Add-ons if we have add-ons available */}
        {addons.length > 0 && (
          <button
            onClick={() => setIsAddonsModalOpen(true)}
            className="hover:underline text-sm font-medium block w-fit"
            style={{ color: effectiveColor }}
          >
            Edit Add-ons
            {selectedAddonIds.length > 0 && (
              <span className="ml-1">({selectedAddonIds.length} selected)</span>
            )}
          </button>
        )}
        {/* Only show Edit Insurance if we have insurance options available */}
        {insuranceOptions.length > 0 && (
          <button
            onClick={() => setIsInsuranceModalOpen(true)}
            className="hover:underline text-sm font-medium block w-fit"
            style={{ color: effectiveColor }}
          >
            Edit Insurance & Protection
            {selectedInsuranceOptionIds.length > 0 && (
              <span className="ml-1">
                ({selectedInsuranceOptionIds.length} selected)
              </span>
            )}
          </button>
        )}
      </div>
    );
  };

  // Fallback rendering for when no pricing data is loaded yet (but dates selected)
  const renderPlaceholderItems = () => (
    <div className="space-y-3 text-sm opacity-60">
      <div className="flex justify-between text-[#020817]">
        <span>Vehicle ({nights} nights)</span>
        <span className="font-medium">
          ${(pricePerNight * nights).toFixed(2)}
        </span>
      </div>
    </div>
  );

  const hasUnavailableInRange = (() => {
    if (!dateRange || !dateRange[0] || !dateRange[1] || !unavailableDates?.size)
      return false;
    const start = new Date(dateRange[0] + "T00:00:00");
    const end = new Date(dateRange[1] + "T00:00:00");
    const current = new Date(start);
    // Unavailable dates are occupied nights; the check-out day is not a night,
    // so it may be booked by the next renter (same-day turnover).
    while (current < end) {
      if (unavailableDates.has(toDateString(current))) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  })();

  const showMinStayError = isDateSelected && nights < minStay;
  const showMaxStayError = isDateSelected && nights > maxStay;

  return (
    <>
      <Card className="shadow-sm border-gray-200 sticky top-6">
        <CardContent className="p-6 space-y-6">
          {/* Header Price */}
          <div className="flex items-center gap-2">
            <Zap className="fill-yellow-400 text-yellow-400 h-6 w-6" />
            <div className="flex items-baseline gap-1 items-center">
              <span
                data-testid="booking-summary-nightly-rate"
                className="text-3xl font-bold text-[#020817]"
              >
                ${headerNightlyRate.toFixed(2)}
              </span>
              <span className="text-gray-500 font-medium mt-1">/ night</span>
            </div>
          </div>

          {/* Date Selection */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-[#020817]">
              Select Dates
            </label>
            <DateRangeInput
              value={dateRange}
              onChange={(dates) =>
                handleDateChange(dates as [string, string] | null)
              }
              placeholder="Pick dates"
              className="w-full"
              primaryColor={effectiveColor}
              theme="light"
              unavailableDates={unavailableDates}
            />

            {showMinStayError && (
              <div className="bg-red-50 border border-red-100 rounded-md p-3 flex gap-3 text-red-600 mt-2">
                <Info className="h-5 w-5 shrink-0 mt-0.5" />
                <p className="text-sm font-medium">
                  Minimum stay is {minStay} nights. Please select more dates.
                </p>
              </div>
            )}

            {showMaxStayError && (
              <div className="bg-red-50 border border-red-100 rounded-md p-3 flex gap-3 text-red-600 mt-2">
                <Info className="h-5 w-5 shrink-0 mt-0.5" />
                <p className="text-sm font-medium">
                  Maximum stay is {maxStay} nights. Please select fewer dates.
                </p>
              </div>
            )}

            {hasUnavailableInRange && (
              <div className="bg-red-50 border border-red-100 rounded-md p-3 flex gap-3 text-red-600 mt-2">
                <Info className="h-5 w-5 shrink-0 mt-0.5" />
                <p className="text-sm font-medium">
                  Your selected dates include unavailable dates. Please adjust
                  your selection.
                </p>
              </div>
            )}

            <div className="flex justify-between text-xs text-slate-500 pt-1 flex-col">
              <span>Min stay: {minStay} night(s)</span>
              <span>Max stay: {maxStay} night(s)</span>
            </div>
          </div>

          {/* Rental Type */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-[#020817]">
              Rental Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleRentalTypeChange("pickup")}
                style={
                  rentalType === "pickup"
                    ? {
                        borderColor: effectiveColor,
                        backgroundColor: hexToRgba(effectiveColor, 0.08),
                      }
                    : undefined
                }
                className={`flex flex-col items-center justify-center p-3 rounded-lg border transition-all ${
                  rentalType === "pickup"
                    ? ""
                    : "border-gray-200 hover:border-gray-300 text-gray-700"
                }`}
              >
                <span
                  className="font-semibold text-sm"
                  style={
                    rentalType === "pickup"
                      ? { color: effectiveColor }
                      : undefined
                  }
                >
                  Pick-up (Free)
                </span>
                <span
                  className={`text-xs mt-0.5 ${rentalType === "pickup" ? "" : "text-gray-500"}`}
                  style={
                    rentalType === "pickup"
                      ? { color: hexToRgba(effectiveColor, 0.7) }
                      : undefined
                  }
                >
                  {pickupLocation || "Pick-up location"}
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleRentalTypeChange("delivery")}
                disabled={!deliveryAvailable}
                style={
                  rentalType === "delivery"
                    ? {
                        borderColor: effectiveColor,
                        backgroundColor: hexToRgba(effectiveColor, 0.08),
                      }
                    : undefined
                }
                className={`flex flex-col items-center justify-center p-3 rounded-lg border transition-all ${
                  rentalType === "delivery"
                    ? ""
                    : deliveryAvailable
                      ? "border-gray-200 hover:border-gray-300 text-gray-700"
                      : "border-gray-200 text-gray-700 opacity-50 cursor-not-allowed"
                }`}
              >
                <span
                  className="font-semibold text-sm"
                  style={
                    rentalType === "delivery"
                      ? { color: effectiveColor }
                      : undefined
                  }
                >
                  Delivery
                </span>
                <span
                  className={`text-xs mt-0.5 text-center px-1 ${rentalType === "delivery" ? "" : "text-gray-500"}`}
                  style={
                    rentalType === "delivery"
                      ? { color: hexToRgba(effectiveColor, 0.7) }
                      : undefined
                  }
                >
                  {!deliveryAvailable ? (
                    <span className="text-red-600">Not available</span>
                  ) : rentalType === "delivery" &&
                    deliveryOption === "location" &&
                    selectedLocation ? (
                    selectedLocation.location_name
                  ) : rentalType === "delivery" && deliveryAddress.city ? (
                    `${deliveryAddress.city}, ${deliveryAddress.state}`
                  ) : customAddressAvailable ? (
                    "within 200 miles"
                  ) : (
                    "to listed locations"
                  )}
                </span>
              </button>
            </div>
          </div>

          <Separator className="bg-gray-200" />

          {/* Cost Breakdown */}
          {isLoadingPricing ? (
            <div className="space-y-3 py-2 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ) : pricingBreakdown ? (
            renderLineItems()
          ) : (
            renderPlaceholderItems()
          )}

          <Separator className="bg-gray-200" />

          {/* Total */}
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-[#020817]">Total</span>
            <span className="text-xl font-bold text-[#020817]">
              {isFinite(total) ? `$${total.toFixed(2)}` : "N/A"}
            </span>
          </div>

          {/* Book Button */}
          <div className="space-y-3 pt-2">
            <Button
              className="w-full h-12 text-base font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: effectiveColor }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor =
                  darkenHex(effectiveColor))
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = effectiveColor)
              }
              onClick={onContinue}
              disabled={
                hasUnavailableInRange || showMinStayError || showMaxStayError
              }
            >
              {requestToBook ? "Request to Book" : "Book now"}
            </Button>
            <p className="text-center text-xs text-gray-500">
              {requestToBook
                ? REQUEST_TO_BOOK_BANK_NOTICE
                : "You won't be charged yet"}
            </p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDeliveryModalOpen} onOpenChange={setIsDeliveryModalOpen}>
        <DialogContent
          className="sm:max-w-md max-h-[90vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 bg-gray-100 rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 17h4V5H2v12h3" />
                  <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5" />
                  <path d="M14 17h1" />
                  <circle cx="7.5" cy="17.5" r="2.5" />
                  <circle cx="17.5" cy="17.5" r="2.5" />
                </svg>
              </div>
              {customAddressAvailable
                ? "Delivery Address"
                : "Delivery Location"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-500 mb-4">
            {customAddressAvailable
              ? "Enter the address where you'd like the RV delivered. Delivery fees will be calculated based on distance from pickup location."
              : "This vehicle is delivered only to its listed delivery locations. Choose the one that suits you."}
          </div>

          {customAddressAvailable && hasDeliveryLocations && (
            <div className="space-y-2 mb-4">
              <Label className="text-sm font-semibold text-[#020817]">
                Delivery Method
              </Label>
              <RadioGroup
                value={deliveryOption}
                onValueChange={(value) =>
                  setDeliveryOption(value as "custom" | "location")
                }
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="custom" id="delivery-option-custom" />
                  <Label
                    htmlFor="delivery-option-custom"
                    className="cursor-pointer font-normal"
                  >
                    Enter custom address
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="location"
                    id="delivery-option-location"
                  />
                  <Label
                    htmlFor="delivery-option-location"
                    className="cursor-pointer font-normal"
                  >
                    Choose from predefined locations
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {deliveryOption === "location" && hasDeliveryLocations ? (
            <div className="space-y-2">
              <Label htmlFor="delivery-location-select">
                Delivery Location *
              </Label>
              <Select
                value={selectedLocationId}
                onValueChange={setSelectedLocationId}
              >
                <SelectTrigger id="delivery-location-select">
                  <SelectValue placeholder="Choose a location" />
                </SelectTrigger>
                <SelectContent>
                  {deliveryLocations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.location_name} ($
                      {Number(location.delivery_fee).toFixed(2)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">Street Address *</Label>
                <CustomerAddressAutocomplete
                  value={deliveryAddress.address}
                  onChange={(val) => {
                    setDeliveryAddress({ ...deliveryAddress, address: val });
                    if (val)
                      setFormErrors((prev) => ({ ...prev, address: false }));
                  }}
                  onPlaceSelect={(place) => {
                    setDeliveryAddress({
                      address: place.address,
                      city: place.city,
                      state: place.state,
                      zipCode: place.zipCode,
                      lat: place.lat,
                      lng: place.lng,
                    });
                    // Clear errors when place is selected
                    setFormErrors({});
                  }}
                  placeholder="Start typing an address..."
                  className="w-full"
                  style={
                    formErrors.address
                      ? {
                          border: "2px solid #ef4444",
                          backgroundColor: "#fef2f2",
                          boxShadow: "0 0 0 2px #fecaca",
                        }
                      : undefined
                  }
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    placeholder="City"
                    value={deliveryAddress.city}
                    onChange={(e) => {
                      setDeliveryAddress({
                        ...deliveryAddress,
                        city: e.target.value,
                      });
                      if (e.target.value)
                        setFormErrors((prev) => ({ ...prev, city: false }));
                    }}
                    className=""
                    style={
                      formErrors.city
                        ? {
                            border: "2px solid #ef4444",
                            backgroundColor: "#fef2f2",
                            boxShadow: "0 0 0 2px #fecaca",
                          }
                        : undefined
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    placeholder="State"
                    value={deliveryAddress.state}
                    onChange={(e) => {
                      setDeliveryAddress({
                        ...deliveryAddress,
                        state: e.target.value,
                      });
                      if (e.target.value)
                        setFormErrors((prev) => ({ ...prev, state: false }));
                    }}
                    className=""
                    style={
                      formErrors.state
                        ? {
                            border: "2px solid #ef4444",
                            backgroundColor: "#fef2f2",
                            boxShadow: "0 0 0 2px #fecaca",
                          }
                        : undefined
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip">ZIP *</Label>
                  <Input
                    id="zip"
                    placeholder="ZIP"
                    value={deliveryAddress.zipCode}
                    onChange={(e) => {
                      setDeliveryAddress({
                        ...deliveryAddress,
                        zipCode: e.target.value,
                      });
                      if (e.target.value)
                        setFormErrors((prev) => ({ ...prev, zipCode: false }));
                    }}
                    className=""
                    style={
                      formErrors.zipCode
                        ? {
                            border: "2px solid #ef4444",
                            backgroundColor: "#fef2f2",
                            boxShadow: "0 0 0 2px #fecaca",
                          }
                        : undefined
                    }
                  />
                </div>
              </div>

              {/* Only show delivery pricing if vehicle has configured values */}
              {deliveryBaseFee !== undefined &&
                deliveryFeePerMile !== undefined &&
                deliveryMaxRadius !== undefined && (
                  <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700">
                    <h5
                      className="font-semibold mb-2"
                      style={{ color: effectiveColor }}
                    >
                      Delivery Pricing:
                    </h5>
                    <ul className="space-y-1 list-disc list-inside text-gray-600">
                      <li>Base fee: ${deliveryBaseFee.toFixed(2)}</li>
                      <li>Per mile: ${deliveryFeePerMile.toFixed(2)}/mile</li>
                      <li>Standard radius: {deliveryMaxRadius} miles</li>
                    </ul>
                  </div>
                )}
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:justify-end mt-6">
            <Button variant="outline" onClick={cancelDeliverySelection}>
              Cancel
            </Button>
            <Button
              onClick={
                deliveryOption === "location" && hasDeliveryLocations
                  ? confirmDeliveryLocation
                  : confirmDeliveryAddress
              }
              className="text-white"
              style={{ backgroundColor: effectiveColor }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor =
                  darkenHex(effectiveColor))
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = effectiveColor)
              }
            >
              {deliveryOption === "location" && hasDeliveryLocations
                ? "Confirm Location"
                : "Confirm Address"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add-ons Modal */}
      <Dialog open={isAddonsModalOpen} onOpenChange={setIsAddonsModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add-ons</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {isDateSelected
                ? `Optional extras for ${nights} ${nights === 1 ? "night" : "nights"}`
                : "Select optional add-ons to enhance your rental experience."}
            </p>
          </DialogHeader>

          <AddonSelector
            addons={addons}
            selectedAddonIds={selectedAddonIds}
            onSelectionChange={(ids) => {
              if (onAddonsChange) {
                onAddonsChange(ids);
              }
            }}
            isLoading={isLoadingAddons}
            nights={isDateSelected ? nights : undefined}
            onDone={() => setIsAddonsModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Insurance & Protection Modal */}
      <Dialog
        open={isInsuranceModalOpen}
        onOpenChange={setIsInsuranceModalOpen}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Insurance & Protection</DialogTitle>
            <p className="text-sm text-gray-500">
              Select insurance options to protect your rental.
            </p>
          </DialogHeader>

          <div className="py-4">
            <InsuranceOptionSelector
              options={insuranceOptions}
              groups={insuranceGroups}
              selectedIds={selectedInsuranceOptionIds}
              onChange={(ids) => {
                if (onInsuranceOptionsChange) {
                  onInsuranceOptionsChange(ids);
                }
              }}
              isLoading={isLoadingInsurance}
            />
          </div>

          <DialogFooter>
            <Button
              onClick={() => setIsInsuranceModalOpen(false)}
              className="text-white"
              style={{ backgroundColor: effectiveColor }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor =
                  darkenHex(effectiveColor))
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = effectiveColor)
              }
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
